import * as React from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@material-ui/core/IconButton";
import Button from "@mui/material/Button";
import CloseIcon from "@material-ui/icons/Close";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import { styled } from "@mui/material/styles";
import TableRow from "@mui/material/TableRow";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { Typography } from "@material-ui/core";
import { useEffect } from "react";
import { useState } from "react";
import ReactToPrint from "react-to-print";
import PrintIcon from "@mui/icons-material/LocalPrintshopOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import BlockIcon from "@mui/icons-material/Block";
import EditIcon from "@mui/icons-material/Edit";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { formatDate } from "../../../../common/components/Helper/Utils";
import VoidPaymentModal from "./VoidPaymentModal";
import EditPaymentModal from "./EditPaymentModal";
import MultipleInvoiceVoidModal from "./MultipleInvoiceVoidModal";

const columns = [
  { id: "index", label: "S No", minWidth: 50 },
  { id: "createdon", label: "Date", minWidth: 100 },
  {
    id: "amount",
    label: "Payment Amount ($)",
    minWidth: 120,
  },
  {
    id: "paymentmethod",
    label: "Payment Method",
    minWidth: 120,
  },
  {
    id: "paymentmethod_details",
    label: "Payment Details",
    minWidth: 120,
  },
  {
    id: "adjust_notes",
    label: "Adjustment Notes",
    minWidth: 120,
  },
  {
    id: "status",
    label: "Status",
    minWidth: 90,
  },
  {
    id: "actions",
    label: "Actions",
    minWidth: 90,
  },
];

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const BootstrapTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.common.black,
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.black,
  },
}));

const getDisplayPaymentStatus = (invoice) => {
  if (!invoice) return "-";
  if (invoice.status && invoice.status !== "Active") {
    return invoice.status;
  }
  return invoice.paymentstatus || "-";
};

const getPaymentId = (payment) =>
  payment?.id ?? payment?.paymentid ?? payment?.paymentId ?? null;

const isTruthyFlag = (value) =>
  value === true || value === 1 || value === "1" || value === "true";

const isPaymentVoided = (payment) => {
  const status = (payment?.status || payment?.paymentstatus || "")
    .toString()
    .toLowerCase();
  return (
    status === "void" ||
    status === "voided" ||
    isTruthyFlag(payment?.is_voided) ||
    isTruthyFlag(payment?.isVoided) ||
    isTruthyFlag(payment?.isvoid) ||
    isTruthyFlag(payment?.is_void) ||
    isTruthyFlag(payment?.voided) ||
    payment?.voided_on != null ||
    payment?.voidedOn != null
  );
};

/** Void payment policy:
 *  Allowed: normal payments, Shopify, QBO-linked (WatchOps voids; QBO may need manual void)
 *  Blocked: Trade (only), Adjust, Payback, already voided, non-Active invoice
 */
export const canVoidThisPayment = (payment, invoice) => {
  if (!payment || !invoice) return { allowed: false, reason: "Invalid payment" };
  if (invoice?.status && invoice.status !== "Active") {
    return { allowed: false, reason: "Invoice is not Active" };
  }
  if (isPaymentVoided(payment)) {
    return { allowed: false, reason: "Payment already voided" };
  }
  if ((payment?.adjust_notes || "").toString().toLowerCase() === "payback") {
    return { allowed: false, reason: "Payback transactions cannot be voided in v1" };
  }
  const method = (payment.paymentmethod || "").toLowerCase();
  if (method === "trade") {
    return { allowed: false, reason: "Trade payments cannot be voided" };
  }
  if (method === "adjust") {
    return { allowed: false, reason: "Adjustment entries cannot be voided here" };
  }
  return { allowed: true, reason: null };
};

/** Edit payment policy:
 *  Allowed: normal payments, Trade, Shopify, QBO-linked
 *  Blocked: Adjust, Payback, voided, non-Active invoice
 */
export const canEditThisPayment = (payment, invoice) => {
  if (!payment || !invoice) return { allowed: false, reason: "Invalid payment" };
  if (invoice?.status && invoice.status !== "Active") {
    return { allowed: false, reason: "Invoice is not Active" };
  }
  if (isPaymentVoided(payment)) {
    return { allowed: false, reason: "Voided payments cannot be edited" };
  }
  if ((payment?.adjust_notes || "").toString().toLowerCase() === "payback") {
    return { allowed: false, reason: "Payback transactions cannot be edited in v1" };
  }
  const method = (payment.paymentmethod || "").toLowerCase();
  if (method === "adjust") {
    return { allowed: false, reason: "Adjustment entries cannot be edited here" };
  }
  return { allowed: true, reason: null };
};

export default function InvoiceModal(props) {
  const { open, handleClose, invoice, getPaymentHistory,canVoidPayment = false,canEditPayment = false, onPaymentVoided, onPaymentEdited, } = props;
  const [rows, setRow] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [voidPaymentOpen, setVoidPaymentOpen] = useState(false);
  const [editPaymentOpen, setEditPaymentOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [voidedIds, setVoidedIds] = useState([]);
  const [actionAnchor, setActionAnchor] = useState(null);
  const [actionPayment, setActionPayment] = useState(null);
  const [multiInvoiceVoidOpen, setMultiInvoiceVoidOpen] = useState(false);
  const [voidScope, setVoidScope] = useState(null); 

  // Payment is voided if backend flags it OR it was just voided in this session.
  const isRowVoided = (payment) =>
    isPaymentVoided(payment) || voidedIds.includes(getPaymentId(payment));

  const canManagePayments = canVoidPayment || canEditPayment;

  // Status + Actions only for roles that can void/edit payments (Admin/Owner/Expert).
  const visibleColumns = canManagePayments
    ? columns
    : columns.filter(
        (column) => column.id !== "status" && column.id !== "actions"
      );

  const loadPaymentHistory = () => {
    if (!invoice?.id || !getPaymentHistory) return;
    setHistoryLoading(true);
    getPaymentHistory({ invoiceId: invoice.id }, (error, res) => {
      setHistoryLoading(false);
      if (error || !res?.data) {
        setRow([]);
        return;
      } 
            const response = res.data;
            const adjustedPaymentIndex = response.findIndex((item) => item.paymentmethod === "Adjust");

            if (adjustedPaymentIndex !== -1) {
              const adjustedPayment = response[adjustedPaymentIndex];
              const filteredPayments = response.filter((item) => item.paymentmethod !== "Adjust");
              filteredPayments.splice(adjustedPaymentIndex, 0, adjustedPayment);
              setRow(filteredPayments);
            } else {
              setRow(response);
            }
          });
        };

  useEffect(() => {
    if (open && invoice?.id) {
      setVoidedIds([]);
      loadPaymentHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, invoice?.id]);

  const handleAfterPrint = React.useCallback(() => {}, []);
 const handleOnBeforeGetContent = React.useCallback(() => Promise.resolve(), []);
  const handleBeforePrint = React.useCallback(() => {}, []);

  const openVoidPayment = (payment) => {
    setSelectedPayment(payment);
    if (payment?.ismultiplepaymnet) {
      setMultiInvoiceVoidOpen(true);
    } else {
      setVoidPaymentOpen(true);
    }
  };

  const handleVoidThisInvoiceOnly = () => {
    setMultiInvoiceVoidOpen(false);
    setVoidScope("single");
    setVoidPaymentOpen(true);
  };

  const handleVoidAllInvoices = () => {
    setMultiInvoiceVoidOpen(false);
    setVoidScope("all");
    setVoidPaymentOpen(true);
  };

  const openEditPayment = (payment) => {
    setSelectedPayment(payment);
    setEditPaymentOpen(true);
  };

  const openPaymentActions = (event, payment) => {
    setActionAnchor(event.currentTarget);
    setActionPayment(payment);
  };

  const closePaymentActions = () => {
    setActionAnchor(null);
    setActionPayment(null);
  };

  const handleVoidPaymentComplete = (results) => {
    const voidedId = getPaymentId(selectedPayment);
    if (voidedId !== null) {
      setVoidedIds((prev) =>
        prev.includes(voidedId) ? prev : [...prev, voidedId]
      );
    }
    setVoidPaymentOpen(false);
    setSelectedPayment(null);
    loadPaymentHistory();
    onPaymentVoided?.(results);
  };

  const handleEditPaymentComplete = (results) => {
    setEditPaymentOpen(false);
    setSelectedPayment(null);
    loadPaymentHistory();
    onPaymentEdited?.(results);
  };

  const renderStatus = (payment) => {
    if (isRowVoided(payment)) {
      const voidReason =
        payment?.void_reason || payment?.voidReason || "Not available";
      return (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "4px",
          }}
        >
          <span style={{ color: "#F76568", fontWeight: 600 }}>Void</span>
          <BootstrapTooltip title={`Void Reason: ${voidReason}`}>
            <span style={{ display: "inline-flex", alignItems: "center" }}>
              <InfoOutlinedIcon
                style={{
                  color: "rgba(0, 0, 0, 0.54)",
                  cursor: "pointer",
                  fontSize: 14,
                }}
              />
            </span>
          </BootstrapTooltip>
        </span>
      );
    }
    return <span style={{ color: "#000" }}>Active</span>;
  };

  const renderActions = (payment) => {
    if (!canManagePayments) return "-";
    const voidPolicy = canVoidThisPayment(payment, invoice);
    const editPolicy = canEditThisPayment(payment, invoice);
    const menuOpen =
      Boolean(actionAnchor) &&
      getPaymentId(actionPayment) === getPaymentId(payment);

    return (
      <>
        <Button
          variant="text"
          disableElevation
          onClick={(event) => openPaymentActions(event, payment)}
          endIcon={<KeyboardArrowDownIcon />}
          className="noprint"
          style={{
            color: "#1976d2",
            minWidth: 95,
            textDecoration: "none",
            textTransform: "none",
          }}
        >
          Actions
        </Button>
        <Menu
          anchorEl={actionAnchor}
          open={menuOpen}
          onClose={closePaymentActions}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{
            style: {
              borderRadius: 6,
              minWidth: 180,
            },
          }}
        >
          <MenuItem
            disabled={!editPolicy.allowed}
            title={!editPolicy.allowed ? editPolicy.reason || "" : ""}
            onClick={() => {
              closePaymentActions();
              openEditPayment(payment);
            }}
          >
            <EditIcon
              style={{
                color: "rgba(0, 0, 0, 0.54)",
                fontSize: 18,
                marginRight: 12,
              }}
            />
            Edit Payment
          </MenuItem>
          <MenuItem
            disabled={!voidPolicy.allowed}
            title={!voidPolicy.allowed ? voidPolicy.reason || "" : ""}
            onClick={() => {
              closePaymentActions();
              openVoidPayment(payment);
            }}
          >
            <BlockIcon
              style={{
                color: "rgba(0, 0, 0, 0.54)",
                fontSize: 18,
                marginRight: 12,
              }}
            />
            Void Payment
          </MenuItem>
        </Menu>
      </>
    );
  };

  const getCellValue = (column, row, index) => {
    if (column.id === "index") return index + 1;
    if (column.id === "status") return renderStatus(row);
    if (column.id === "actions") return renderActions(row);
    if (column.id === "createdon" && row.createdon) {
      return formatDate(row.createdon, "MM/DD/YYYY");
    }
    return row[column.id] || "-";
  };

  return (
    <div>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth={"md"}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <div id="printable-content">
          <div>
            <DialogTitle id="alert-dialog-title ">
              Payment History
              <IconButton
                onClick={() => {
                  handleClose();
                }}
                style={{ float: "right" }}
                className="noprint"
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: "20px",
              marginRight: "20px",
              marginBottom: "20px",
            }}
          >
            <ReactToPrint
              onBeforeGetContent={handleOnBeforeGetContent}
              onAfterPrint={handleAfterPrint}
              onBeforePrint={handleBeforePrint}
              removeAfterPrint
              trigger={() => (
                <Button
                  variant="contained"
                  color="primary"
                  className="text-capitalize primary-bg color-white noprint"
                  startIcon={<PrintIcon />}
                >
                  PRINT
                </Button>
              )}
              content={() => document.getElementById("printable-content")}
            />
          </div>
          <DialogContent>
            <DialogContentText
              id="alert-dialog-description"
              style={{ marginTop: "-20px" }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  marginBottom: "20px",
                }}
              >
                <div>
                  <div>
                    <Typography variant="subtitle2" style={{textTransform:"capitalize"}}>
                      Name: {invoice?.contactname || "-"}{" "}
                    </Typography>
                  </div>
                  <div>
                    <Typography variant="subtitle2">
                      Invoice ID: {invoice?.id || "-"}{" "}
                    </Typography>
                  </div>
                  <div>
                    <Typography variant="subtitle2">
                      Payment status: {getDisplayPaymentStatus(invoice)}{" "}
                    </Typography>
                  </div>
                </div>
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <div>
                    <Typography variant="subtitle2">
                      Invoice Amount: {invoice?.totalprice || "-"}
                    </Typography>
                  </div>
                  <div>
                    <Typography variant="subtitle2">
                      Balance: {invoice?.balance || "-"}
                    </Typography>
                  </div>
                </div>
              </div>
              <TableContainer>
                <Table aria-label="sticky table">
                  <TableHead>
                    <TableRow>
                      {visibleColumns.map((column) => (
                        <StyledTableCell
                          key={column.id}
                          align={column.align}
                          style={{ minWidth: column.minWidth }}
                          className={
                            column.id === "actions" ? "noprint" : undefined
                          }
                        >
                          {column.label}
                        </StyledTableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.length ? (
                      <>
                        {rows.map((row, index) => {
                          return (
                            <StyledTableRow hover tabIndex={-1} key={row.id || row.paymentid || index}>
                              {visibleColumns.map((column) => {
                                const isPayback =
                                  row?.adjust_notes === "Payback";
                                return (
                                   <TableCell
                                    key={column.id}
                                    align={column.align}
                                    className={
                                      column.id === "actions"
                                        ? "noprint"
                                        : undefined
                                    }
                                    style={{
                                      fontStyle:
                                       (column.id === "adjust_notes" &&isPayback) ||
                                       (column.id === "amount" && isPayback)
                                          ? "italic"
                                          : "normal",
                                        color:
                                          (column.id === "adjust_notes" &&isPayback) ||
                                          (column.id === "amount" && isPayback)
                                            ? "#d54949"
                                            : "inherit",
                                    }}
                                  >
                                    {getCellValue(column, row, index)}
                                    </TableCell>
                                );
                              })}
                            </StyledTableRow>
                          );
                        })}
                      </>
                    ) : (
                      <TableCell colSpan={visibleColumns.length} style={{ textAlign: "center" }}>
                        <Typography variant="h6" className="color-BCBCBC">
                          {historyLoading
                            ? "Loading..."
                            : "No Transactions Found"}
                        </Typography>
                      </TableCell>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </DialogContentText>
          </DialogContent>
        </div>
      </Dialog>

      {voidPaymentOpen && selectedPayment && (
        <VoidPaymentModal
          open={voidPaymentOpen}
          handleClose={() => {
            setVoidPaymentOpen(false);
            setSelectedPayment(null);
            setVoidScope(null);
          }}
          invoice={invoice}
          payment={selectedPayment}
          onVoidComplete={handleVoidPaymentComplete}
          isMultiplePayment={!!selectedPayment?.ismultiplepaymnet}
          voidScope={voidScope}
        />
      )}

      {multiInvoiceVoidOpen && selectedPayment && (
        <MultipleInvoiceVoidModal
          open={multiInvoiceVoidOpen}
          handleClose={() => {
            setMultiInvoiceVoidOpen(false);
            setSelectedPayment(null);
          }}
          payment={selectedPayment}
          onVoidThisInvoiceOnly={handleVoidThisInvoiceOnly}
          onVoidAllInvoices={handleVoidAllInvoices}
        />
      )}

      {editPaymentOpen && selectedPayment && (
        <EditPaymentModal
          open={editPaymentOpen}
          handleClose={() => {
            setEditPaymentOpen(false);
            setSelectedPayment(null);
          }}
          invoice={invoice}
          payment={selectedPayment}
          onEditComplete={handleEditPaymentComplete}
        />
      )}
    </div>
  );
}
